import React from "react";
import * as Icon from "react-feather";
import clsx from "clsx";

import "./allocation-review-modal.css";

import BookkeepingService, { ISSUE_STATUSES, REVIEW_STATUS_FILTER_OPTIONS } from "../../../../services/BookkeepingService";
import UserService from "../../../../services/UserService";
import UtilityService from "../../../../services/UtilityService";

import WComponent from "../../../common/WComponent";
import Checkbox from "../../../common/checkbox/Checkbox";
import TextAreaField from "../../../common/text_area_field/TextAreaField";
import SearchableDropdown from "../../../common/searchable_dropdown/SearchableDropdown";
import ToolsModal from "../kit/ToolsModal";
import ToolsSelect from "../kit/ToolsSelect";
import BulkClassifyModal from "./BulkClassifyModal";
import ClassifyTransactionModal from "./ClassifyTransactionModal";

class AllocationReviewModal extends WComponent {
	constructor(props) {
		super(props);

		this.state = {
			expandedDecisionKeys: {},
			selectedTransactionKeys: {},
			notingDecisionKey: null,
			notingTransaction: null,
			notingIssueKey: null,
			transactionsBeingFlagged: null,
			transactionSortColumn: "date",
			transactionSortAscending: true,
			priorSortColumn: "date",
			priorSortAscending: true,
			reviewNoteDraft: "",
			reviewSearchText: "",
			reclassifyingTransaction: null,
			reviewStatusFilter: "",
			expandedGlAccounts: {},
			issuesViewOpen: props.initialView === "issues",
			raisingIssueOpen: false,
			raiseIssueDecisionKey: "",
			trialBalanceOpen: false,
			trialBalanceLoading: false,
			trialBalanceRows: [],
			trialBalanceMetadata: null,
			selectedTrialBalanceCode: "",
			reviewSectionOpen: true,
		};
	}

	onFilterByTrialBalanceAccount = row => {
		const alreadyFilteringThisAccount = this.state.selectedTrialBalanceCode === row.canonicalCode;
		if (alreadyFilteringThisAccount) {
			this.update({ selectedTrialBalanceCode: "" });
			return;
		}

		const decisions = BookkeepingService.buildReviewDecisions({ transactions: this.props.transactions });
		const decisionsForAccount = BookkeepingService.getDecisionsForCanonicalCode({ decisions, canonicalCode: row.canonicalCode });

		const expandedDecisionKeys = { ...this.state.expandedDecisionKeys };
		for (const decision of decisionsForAccount) expandedDecisionKeys[decision.decisionKey] = true;

		this.update({ selectedTrialBalanceCode: row.canonicalCode, reviewSearchText: "", reviewSectionOpen: true, expandedDecisionKeys });
	};

	onStartRaiseIssue = () => this.update({ raisingIssueOpen: true, raiseIssueDecisionKey: "", reviewNoteDraft: "", notingDecisionKey: null });

	onCancelRaiseIssue = () => this.update({ raisingIssueOpen: false, raiseIssueDecisionKey: "", reviewNoteDraft: "" });

	onSelectRaiseIssueAccount = item => this.update({ raiseIssueDecisionKey: item.decisionKey });

	onRaiseIssueSubmit = async decisions => {
		const { transactions, onTransactionsChange } = this.props;

		const decision = decisions.find(candidate => candidate.decisionKey === this.state.raiseIssueDecisionKey);
		if (!decision) return;

		const author = this.getReviewerName();
		const commentedAt = new Date().toISOString();
		const updatedTransactions = BookkeepingService.addReviewCommentToTransactions({
			transactions,
			transactionsToComment: decision.transactions,
			author,
			text: this.state.reviewNoteDraft,
			commentedAt,
		});

		await this.update({ raisingIssueOpen: false, raiseIssueDecisionKey: "", reviewNoteDraft: "" });
		await onTransactionsChange(updatedTransactions);
	};

	openIssuesView = () => this.update({ issuesViewOpen: true, notingDecisionKey: null, notingIssueKey: null, reviewNoteDraft: "" });

	closeIssuesView = () => this.update({ issuesViewOpen: false, notingDecisionKey: null, notingIssueKey: null, reviewNoteDraft: "" });

	onSetIssueStatus = async (issue, status) => {
		const { transactions, onTransactionsChange } = this.props;

		const updatedTransactions = BookkeepingService.setReviewIssueStatusOnTransactions({ transactions, transactionsToUpdate: issue.transactions, status });

		await onTransactionsChange(updatedTransactions);
	};

	getCurrentTotalsByAccount = decisions => {
		const currentByAccount = {};
		for (const decision of decisions) {
			const joinKey = decision.canonicalCode || decision.classification;
			const existingTotal = currentByAccount[joinKey] || 0;
			currentByAccount[joinKey] = existingTotal + decision.totalAmount;
		}
		return currentByAccount;
	};

	onToggleGlAccount = account => {
		const expandedGlAccounts = { ...this.state.expandedGlAccounts };
		if (expandedGlAccounts[account]) {
			delete expandedGlAccounts[account];
		} else {
			expandedGlAccounts[account] = true;
		}
		this.update({ expandedGlAccounts });
	};

	componentDidUpdate(prevProps) {
		if (!this.state.trialBalanceOpen || this.props.unmappedAccountCount > 0) return;
		const becameMapped = prevProps.unmappedAccountCount > 0 && this.props.unmappedAccountCount === 0;
		const transactionsChanged = prevProps.transactions !== this.props.transactions;
		if (becameMapped || transactionsChanged) this.loadTrialBalance();
	}

	onToggleTrialBalance = async () => {
		const willOpen = !this.state.trialBalanceOpen;
		await this.update({ trialBalanceOpen: willOpen });
		if (willOpen && this.props.unmappedAccountCount === 0) await this.loadTrialBalance();
	};

	onToggleReviewSection = () => this.update({ reviewSectionOpen: !this.state.reviewSectionOpen });

	loadTrialBalance = async () => {
		const { clientId, transactions, priorTrialBalanceEntries } = this.props;

		await this.update({ trialBalanceLoading: true });
		const result = await BookkeepingService.getTrialBalance({ clientId, transactions, priorEntries: priorTrialBalanceEntries });
		if (!result) {
			await this.update({ trialBalanceLoading: false, trialBalanceRows: [], trialBalanceMetadata: null });
			return;
		}

		const trialBalanceRows = BookkeepingService.buildTrialBalanceRows({ trialBalance: result.trialBalance, priorTrialBalance: result.priorTrialBalance });
		await this.update({ trialBalanceLoading: false, trialBalanceRows, trialBalanceMetadata: result.metadata });
	};

	renderTrialBalanceRow = (row, index) => {
		const accountKind = BookkeepingService.getAccountNature({ canonicalCode: row.canonicalCode, accountName: row.account });
		const isFilteringThisAccount = this.state.selectedTrialBalanceCode === row.canonicalCode;

		return (
			<div
				key={index}
				className={clsx("bk-review__tb-row bk-review__tb-row--clickable", isFilteringThisAccount && "bk-review__tb-row--active")}
				onClick={() => this.onFilterByTrialBalanceAccount(row)}
			>
				<span className="bk-review__tb-account">{row.account}</span>
				<span className="bk-review__tb-gifi">{row.gifiCode}</span>
				<span className="bk-review__tb-amount">{row.current ? UtilityService.formatCurrency(row.current) : "—"}</span>
				<span className="bk-review__tb-amount">{row.prior ? UtilityService.formatCurrency(row.prior) : "—"}</span>
				<span className="bk-review__tb-change">{this.renderPercentChange(row.current, row.prior, accountKind)}</span>
			</div>
		);
	};

	renderTrialBalancePanel = () => {
		const { unmappedAccountCount, onRequestCoaMapping, fsMappingReferenceLoaded } = this.props;
		const { trialBalanceOpen, trialBalanceLoading, trialBalanceRows, trialBalanceMetadata } = this.state;

		const mappingReferenceIsMissing = !fsMappingReferenceLoaded;
		const hasUnmappedAccounts = unmappedAccountCount > 0;
		const hasRows = trialBalanceRows.length > 0;
		const isBalanced = trialBalanceMetadata ? trialBalanceMetadata.is_balanced : null;
		const differenceText = trialBalanceMetadata ? UtilityService.formatCurrency(trialBalanceMetadata.difference) : "";

		return (
			<div className="bk-review__panel">
				<button type="button" className="bk-review__panel-toggle" onClick={this.onToggleTrialBalance}>
					{trialBalanceOpen ? <Icon.ChevronDown size={16} /> : <Icon.ChevronRight size={16} />}
					<span className="bk-review__panel-title">Trial balance</span>
					{mappingReferenceIsMissing && <span className="bk-review__panel-status bk-review__panel-status--off">Unavailable</span>}
					{!mappingReferenceIsMissing && !hasUnmappedAccounts && hasRows && (
						<span className={clsx("bk-review__panel-status", isBalanced ? "bk-review__panel-status--ok" : "bk-review__panel-status--off")}>
							{isBalanced ? "Balanced" : `Out of balance by ${differenceText}`}
						</span>
					)}
				</button>
				{trialBalanceOpen && (
					<div className="bk-review__panel-body bk-review__panel-body--scroll">
						{mappingReferenceIsMissing ? (
							<div className="bk-review__tb-prompt bk-review__tb-prompt--unavailable">
								<span className="bk-review__tb-prompt-text">This client's FS account mapping didn't load, so the trial balance and its prior-year comparatives can't be built. Reload the page and try again.</span>
							</div>
						) : hasUnmappedAccounts ? (
							<div className="bk-review__tb-prompt">
								<span className="bk-review__tb-prompt-text">{unmappedAccountCount} account{unmappedAccountCount === 1 ? " isn't" : "s aren't"} mapped to an FS account yet — the trial balance is built from those mappings.</span>
								<button type="button" className="tk-button tk-button--secondary tk-button--compact" onClick={onRequestCoaMapping}>
									<Icon.Zap size={14} />
									Map accounts
								</button>
							</div>
						) : (
							<>
								{trialBalanceLoading && <div className="bk-review__tb-empty">Building trial balance…</div>}
								{!trialBalanceLoading && !hasRows && <div className="bk-review__tb-empty">No trial balance available.</div>}
								{!trialBalanceLoading && hasRows && (
									<div className="bk-review__tb-table">
										<div className="bk-review__tb-row bk-review__tb-row--header">
											<span className="bk-review__tb-account">Account</span>
											<span className="bk-review__tb-gifi">GIFI</span>
											<span className="bk-review__tb-amount">Current</span>
											<span className="bk-review__tb-amount">Prior</span>
											<span className="bk-review__tb-change">Change</span>
										</div>
										{trialBalanceRows.map(this.renderTrialBalanceRow)}
									</div>
								)}
							</>
						)}
					</div>
				)}
			</div>
		);
	};

	renderTransactionRow = ({ date, description, taxText, amountText, onReclassify, onToggleSelected, isSelected, onRaiseIssue, hasIssue }, index) => {
		return (
			<div key={index} className={clsx("bk-review__transaction-row", hasIssue && "bk-review__transaction-row--flagged")}>
				{onToggleSelected ? (
					<span className="bk-review__transaction-checkbox" onClick={event => event.stopPropagation()}>
						<Checkbox checked={isSelected} onChange={onToggleSelected} />
					</span>
				) : (
					<span className="bk-review__transaction-checkbox-spacer" />
				)}
				<span className="bk-review__transaction-date">{date}</span>
				<span className="bk-review__transaction-description">{description}</span>
				<span className="bk-review__transaction-tax">{taxText}</span>
				<span className="bk-review__transaction-amount">${amountText}</span>
				{onReclassify || onRaiseIssue ? (
					<span className="bk-review__transaction-actions">
						{onRaiseIssue && (
							<button
								className={clsx("bk-review__transaction-action", hasIssue && "bk-review__transaction-action--flagged")}
								onClick={onRaiseIssue}
								title={hasIssue ? "View or reply to this transaction's issue" : "Raise an issue on this transaction"}
							>
								<Icon.MessageSquare size={13} />
							</button>
						)}
						{onReclassify && (
							<button className="bk-review__transaction-action" onClick={onReclassify} title="Reclassify this transaction">
								<Icon.Edit2 size={13} />
							</button>
						)}
					</span>
				) : (
					<span className="bk-review__transaction-reclassify-spacer" />
				)}
			</div>
		);
	};

	renderGeneralLedgerEntry = (entry, index) => {
		const amountText = UtilityService.formatCurrency(entry.amount);
		const taxAmount = Number(entry.tax || 0);
		const taxAmountText = UtilityService.formatCurrency(taxAmount);
		const taxPercent = BookkeepingService.calculateTaxPercent({ taxAmount, netAmount: entry.amount });
		const taxPercentPrefix = taxPercent != null ? `${taxPercent}% · ` : "";
		const taxText = taxAmount > 0 ? `${taxPercentPrefix}$${taxAmountText}` : "No tax";

		return this.renderTransactionRow({ date: entry.date, description: entry.description || "—", taxText, amountText }, index);
	};

	getDecisionAccountKind = decision => {
		const transactionWithCode = (decision.transactions || []).find(transaction => transaction.canonical_code);
		const canonicalCode = transactionWithCode ? transactionWithCode.canonical_code : "";
		return BookkeepingService.getAccountNature({ canonicalCode, accountName: decision.classification });
	};

	renderPercentChange = (currentTotal, priorTotal, accountKind) => {
		if (currentTotal == null) return <span className="bk-review__compare-empty">no current year</span>;

		const currentMagnitude = Math.abs(currentTotal);
		const priorMagnitude = Math.abs(priorTotal);
		const percentChange = UtilityService.calculatePercentChange({ from: priorMagnitude, to: currentMagnitude });

		if (percentChange == null) return <span className="bk-review__compare-change">—</span>;
		if (percentChange === 0) return <span className="bk-review__compare-change">no change</span>;

		const isIncrease = percentChange > 0;
		const arrow = isIncrease ? "▲" : "▼";

		let favorabilityClass = "";
		if (accountKind === "revenue") favorabilityClass = isIncrease ? "bk-review__compare-change--good" : "bk-review__compare-change--bad";
		else if (accountKind === "expense") favorabilityClass = isIncrease ? "bk-review__compare-change--bad" : "bk-review__compare-change--good";

		return <span className={clsx("bk-review__compare-change", favorabilityClass)}>{arrow} {Math.abs(percentChange)}%</span>;
	};

	renderGeneralLedgerGroup = (group, currentByAccount, isSearching) => {
		const isExpanded = isSearching || !!this.state.expandedGlAccounts[group.account];
		const totalAmountText = UtilityService.formatCurrency(group.totalAmount);
		const entryCountText = `${group.entryCount} ${group.entryCount === 1 ? "entry" : "entries"}`;
		const joinKey = group.canonicalCode || group.account;
		const currentTotal = joinKey in currentByAccount ? currentByAccount[joinKey] : null;
		const bankSideCodes = this.props.bankAccountCanonicalCodes || [];
		const isABankSideAccount = !!group.canonicalCode && bankSideCodes.includes(group.canonicalCode);

		return (
			<div key={group.account} className="bk-review__decision">
				<div className="bk-review__decision-main" onClick={() => this.onToggleGlAccount(group.account)}>
					<div className="bk-review__decision-info">
						<span className="bk-review__decision-name">{group.account}</span>
						<span className="bk-review__decision-amount">${totalAmountText}</span>
						<span className="bk-review__decision-meta">{entryCountText}</span>
					</div>
					<div className="bk-review__compare">
						{currentTotal == null && (
							<span className="bk-review__compare-empty">
								{isABankSideAccount ? "money moves through this account, not to it" : "no activity this year"}
							</span>
						)}
					</div>
				</div>
				{isExpanded && (
					<div className="bk-review__decision-transactions">
						{this.renderSortHeader({ columnStateKey: "priorSortColumn", directionStateKey: "priorSortAscending" })}
						{BookkeepingService.sortTransactionsForReview({
							transactions: group.entries,
							column: this.state.priorSortColumn,
							ascending: this.state.priorSortAscending,
						}).map(this.renderGeneralLedgerEntry)}
					</div>
				)}
			</div>
		);
	};

	renderGeneralLedgerPanel = (priorGroups, currentByAccount, priorPeriod, isSearching) => {
		const { generalLedgerEntries } = this.props;

		return (
			<div className="bk-review__gl-panel">
				<div className="bk-review__col-header">
					<span className="bk-review__col-title">Prior year{priorPeriod.yearName ? ` (${priorPeriod.yearName})` : ""}</span>
					<span className="bk-review__col-subtitle">GL Reference{priorPeriod.span ? ` · ${priorPeriod.span}` : ""}</span>
				</div>
				<p className="bk-review__progress">{generalLedgerEntries.length} entries</p>
				<div className="bk-review__bulk-bar" />
				<div className="bk-review__list">
					{priorGroups.length === 0 && <p className="bk-review__empty">No GL entries match the search.</p>}
					{priorGroups.map(group => this.renderGeneralLedgerGroup(group, currentByAccount, isSearching))}
				</div>
			</div>
		);
	};

	getReviewerName = () => {
		const userData = UserService.getUserData();
		if (!userData) return "Reviewer";

		const fullName = `${userData.firstName || ""} ${userData.lastName || ""}`.trim();
		const reviewerName = fullName || "Reviewer";
		return reviewerName;
	};

	getSelectableDecisions = decisions => {
		const selectableDecisions = decisions.filter(decision => decision.isUnclassifiedDecision || !decision.isFullyReviewed);
		return selectableDecisions;
	};

	getApprovableDecisions = decisions => {
		const approvableDecisions = decisions.filter(decision => !decision.isUnclassifiedDecision && !decision.isFullyReviewed);
		return approvableDecisions;
	};

	isTransactionSelected = transaction => {
		const key = BookkeepingService.getTransactionIdentity({ transaction });
		return !!this.state.selectedTransactionKeys[key];
	};

	/**
	 * Whether a group is wholly, partly or not at all selected — what its checkbox has
	 * to show now that the rows inside it can be picked one at a time.
	 */
	getDecisionSelectionState = decision => {
		const selectedCount = decision.transactions.filter(this.isTransactionSelected).length;
		if (selectedCount === 0) return "none";
		if (selectedCount === decision.transactions.length) return "all";
		return "some";
	};

	getSelectedTransactions = decisions => {
		const everyTransaction = decisions.flatMap(decision => decision.transactions);
		const selectedTransactions = everyTransaction.filter(this.isTransactionSelected);
		return selectedTransactions;
	};

	setTransactionsSelected = ({ transactions, isSelected }) => {
		const selectedTransactionKeys = { ...this.state.selectedTransactionKeys };
		for (const transaction of transactions) {
			const key = BookkeepingService.getTransactionIdentity({ transaction });
			if (isSelected) selectedTransactionKeys[key] = true;
			else delete selectedTransactionKeys[key];
		}
		this.update({ selectedTransactionKeys });
	};

	onToggleTransactionSelected = transaction => {
		this.setTransactionsSelected({ transactions: [transaction], isSelected: !this.isTransactionSelected(transaction) });
	};

	onClassifySelectedClicked = decisions => {
		const transactionsBeingClassified = this.getSelectedTransactions(decisions);
		if (transactionsBeingClassified.length === 0) return;
		this.update({ transactionsBeingClassified });
	};

	onRaiseIssueOnSelectedClicked = decisions => {
		const transactionsBeingFlagged = this.getSelectedTransactions(decisions);
		if (transactionsBeingFlagged.length === 0) return;
		this.update({ transactionsBeingFlagged, reviewNoteDraft: "" });
	};

	onBulkIssueClose = () => this.update({ transactionsBeingFlagged: null, reviewNoteDraft: "" });

	onBulkIssueSubmit = async () => {
		const { transactions, onTransactionsChange } = this.props;
		const transactionsToComment = this.state.transactionsBeingFlagged || [];
		if (transactionsToComment.length === 0) return;

		const updatedTransactions = BookkeepingService.addReviewCommentToTransactions({
			transactions,
			transactionsToComment,
			author: this.getReviewerName(),
			text: this.state.reviewNoteDraft,
			commentedAt: new Date().toISOString(),
		});

		await this.update({ transactionsBeingFlagged: null, reviewNoteDraft: "", selectedTransactionKeys: {} });
		await onTransactionsChange(updatedTransactions);
	};

	onBulkClassifyClose = () => this.update({ transactionsBeingClassified: null });

	onBulkClassifyApply = async ({ classification, tax }) => {
		const { onBulkClassify } = this.props;
		const transactionsToClassify = this.state.transactionsBeingClassified || [];
		if (transactionsToClassify.length === 0) return;

		await this.update({ transactionsBeingClassified: null, selectedTransactionKeys: {} });
		await onBulkClassify({ transactions: transactionsToClassify, classification, tax });
	};

	onToggleDecisionSelected = decision => {
		const nothingIsSelectedYet = this.getDecisionSelectionState(decision) !== "all";
		this.setTransactionsSelected({ transactions: decision.transactions, isSelected: nothingIsSelectedYet });
	};

	onToggleSelectAllDecisions = decisions => {
		const selectableDecisions = this.getSelectableDecisions(decisions);
		const everyTransaction = selectableDecisions.flatMap(decision => decision.transactions);
		const allAreSelected = everyTransaction.length > 0 && everyTransaction.every(this.isTransactionSelected);

		this.setTransactionsSelected({ transactions: everyTransaction, isSelected: !allAreSelected });
	};

	onApproveSelectedDecisions = async decisions => {
		const { transactions, onTransactionsChange } = this.props;

		const approvableDecisions = this.getApprovableDecisions(decisions);
		const transactionsToReview = this.getSelectedTransactions(approvableDecisions);
		if (transactionsToReview.length === 0) return;

		const reviewerName = this.getReviewerName();
		const reviewedAt = new Date().toISOString();
		const updatedTransactions = BookkeepingService.stampReviewOnTransactions({ transactions, transactionsToReview, reviewerName, reviewedAt });

		await this.update({ selectedTransactionKeys: {} });
		await onTransactionsChange(updatedTransactions);
	};

	onApproveDecision = async decision => {
		const { transactions, onTransactionsChange } = this.props;

		const reviewerName = this.getReviewerName();
		const reviewedAt = new Date().toISOString();
		const updatedTransactions = BookkeepingService.stampReviewOnTransactions({ transactions, transactionsToReview: decision.transactions, reviewerName, reviewedAt });

		await onTransactionsChange(updatedTransactions);
	};

	onUndoDecisionReview = async decision => {
		const { transactions, onTransactionsChange } = this.props;

		const updatedTransactions = BookkeepingService.clearReviewOnTransactions({ transactions, transactionsToClear: decision.transactions });

		await onTransactionsChange(updatedTransactions);
	};

	onStartReclassifyTransaction = transaction => {
		this.update({ reclassifyingTransaction: transaction });
	};

	onCancelReclassifyTransaction = () => {
		this.update({ reclassifyingTransaction: null });
	};

	/**
	 * Marking a transfer opens the transfer modal, which the table owns. This closes the
	 * classify modal on the way out so the two do not end up stacked on each other.
	 */
	onMarkReclassifiedAsTransfer = async transaction => {
		await this.update({ reclassifyingTransaction: null });
		this.props.onMarkAsTransfer(transaction);
	};

	onReclassifyTransaction = async (transaction, classificationItem) => {
		const { transactions, classificationTaxMap, onTransactionsChange } = this.props;

		const tax = classificationTaxMap[classificationItem.name] || "";
		const reviewerName = this.getReviewerName();
		const reviewedAt = new Date().toISOString();
		const updatedTransactions = BookkeepingService.reclassifyTransactions({
			transactions,
			transactionsToChange: [transaction],
			classification: classificationItem,
			tax,
			reviewerName,
			reviewedAt,
		});

		await this.update({ reclassifyingTransaction: null });
		await onTransactionsChange(updatedTransactions);
	};

	onToggleDecisionDetails = decisionKey => {
		const expandedDecisionKeys = { ...this.state.expandedDecisionKeys };
		if (expandedDecisionKeys[decisionKey]) {
			delete expandedDecisionKeys[decisionKey];
		} else {
			expandedDecisionKeys[decisionKey] = true;
		}
		this.update({ expandedDecisionKeys });
	};

	isEverythingExpanded = (decisions, groups) => {
		const everyDecisionExpanded = decisions.every(decision => this.state.expandedDecisionKeys[decision.decisionKey]);
		const everyGroupExpanded = groups.every(group => this.state.expandedGlAccounts[group.account]);
		const somethingToExpand = decisions.length > 0 || groups.length > 0;
		return somethingToExpand && everyDecisionExpanded && everyGroupExpanded;
	};

	onToggleExpandAll = (decisions, groups) => {
		const collapse = this.isEverythingExpanded(decisions, groups);

		const expandedDecisionKeys = {};
		const expandedGlAccounts = {};
		if (!collapse) {
			for (const decision of decisions) expandedDecisionKeys[decision.decisionKey] = true;
			for (const group of groups) expandedGlAccounts[group.account] = true;
		}
		this.update({ expandedDecisionKeys, expandedGlAccounts });
	};

	onStartDecisionNote = decision => {
		this.update({ notingDecisionKey: decision.decisionKey, notingTransaction: null, reviewNoteDraft: "", reclassifyingTransaction: null });
	};

	isNotingTransaction = ({ transaction }) => {
		const { notingTransaction } = this.state;
		if (!notingTransaction) return false;
		return BookkeepingService.getTransactionIdentity({ transaction: notingTransaction }) === BookkeepingService.getTransactionIdentity({ transaction });
	};

	onStartTransactionNote = transaction => {
		this.update({ notingTransaction: transaction, notingDecisionKey: null, reviewNoteDraft: "", reclassifyingTransaction: null });
	};

	onStartIssueReply = issue => {
		this.update({ notingIssueKey: issue.issueKey, notingDecisionKey: null, notingTransaction: null, reviewNoteDraft: "" });
	};

	onCancelDecisionNote = () => {
		this.update({ notingDecisionKey: null, notingTransaction: null, notingIssueKey: null, reviewNoteDraft: "" });
	};

	onAddDecisionComment = async decision => {
		const { transactions, onTransactionsChange } = this.props;

		const author = this.getReviewerName();
		const commentedAt = new Date().toISOString();
		const updatedTransactions = BookkeepingService.addReviewCommentToTransactions({
			transactions,
			transactionsToComment: decision.transactions,
			author,
			text: this.state.reviewNoteDraft,
			commentedAt,
		});

		await this.update({ notingDecisionKey: null, notingTransaction: null, notingIssueKey: null, reviewNoteDraft: "" });
		await onTransactionsChange(updatedTransactions);
	};

	onSortBy = ({ columnStateKey, directionStateKey, column }) => {
		const alreadySortingByThis = this.state[columnStateKey] === column;
		this.update({
			[columnStateKey]: column,
			[directionStateKey]: alreadySortingByThis ? !this.state[directionStateKey] : true,
		});
	};

	/**
	 * The same header serves both panels — the two lists show the same four columns, and
	 * a reviewer comparing them across the split should not meet two different controls.
	 * Each panel keeps its own sort so one can be read by date while the other is read
	 * by amount.
	 */
	renderSortHeader = ({ columnStateKey, directionStateKey }) => {
		const activeColumn = this.state[columnStateKey];
		const ascending = this.state[directionStateKey];

		const columns = [
			{ key: "date", label: "Date", className: "bk-review__transaction-date" },
			{ key: "description", label: "Description", className: "bk-review__transaction-description" },
			{ key: "tax", label: "Tax", className: "bk-review__transaction-tax" },
			{ key: "amount", label: "Amount", className: "bk-review__transaction-amount" },
		];

		return (
			<div className="bk-review__transaction-row bk-review__transaction-row--header">
				<span className="bk-review__transaction-checkbox-spacer" />
				{columns.map(column => (
					<button
						key={column.key}
						type="button"
						className={clsx("bk-review__sort-button", column.className, activeColumn === column.key && "bk-review__sort-button--active")}
						onClick={() => this.onSortBy({ columnStateKey, directionStateKey, column: column.key })}
					>
						{column.label}
						{activeColumn === column.key && (ascending ? <Icon.ChevronUp size={12} /> : <Icon.ChevronDown size={12} />)}
					</button>
				))}
				<span className="bk-review__transaction-reclassify-spacer" />
			</div>
		);
	};

	renderDecisionTransactions = decision => {
		const { hasTax } = this.props;
		const { transactionSortColumn, transactionSortAscending } = this.state;

		const sortedTransactions = BookkeepingService.sortTransactionsForReview({
			transactions: decision.transactions,
			column: transactionSortColumn,
			ascending: transactionSortAscending,
		});

		return (
			<div className="bk-review__decision-transactions">
				{this.renderSortHeader({ columnStateKey: "transactionSortColumn", directionStateKey: "transactionSortAscending" })}
				{sortedTransactions.map((transaction, index) => {
					const amountText = UtilityService.formatCurrency(transaction.amount);
					const taxAmount = BookkeepingService.calculateTaxAmount({ amount: transaction.amount, tax: transaction.tax, hasTax });
					const taxAmountText = UtilityService.formatCurrency(taxAmount);
					const taxText = transaction.tax ? `${transaction.tax} · $${taxAmountText}` : "No tax";
					const issue = BookkeepingService.readReviewIssue({ transaction });
					const isNotingThisTransaction = this.isNotingTransaction({ transaction });

					return (
						<React.Fragment key={index}>
							{this.renderTransactionRow({
								date: transaction.date,
								description: transaction.description,
								taxText,
								amountText,
								onReclassify: () => this.onStartReclassifyTransaction(transaction),
								onToggleSelected: () => this.onToggleTransactionSelected(transaction),
								isSelected: this.isTransactionSelected(transaction),
								onRaiseIssue: () => this.onStartTransactionNote(transaction),
								hasIssue: issue.comments.length > 0,
							}, index)}
							{isNotingThisTransaction && (
								<div className="bk-review__transaction-note">
									{issue.comments.length > 0 && this.renderCommentThread(issue.comments)}
									{this.renderCommentEditor({ transactions: [transaction] }, issue.comments.length > 0)}
								</div>
							)}
						</React.Fragment>
					);
				})}
			</div>
		);
	};

	renderReclassifyModal = () => {
		const { reclassifyingTransaction } = this.state;
		if (!reclassifyingTransaction) return null;

		return (
			<ClassifyTransactionModal
				transaction={reclassifyingTransaction}
				classifications={this.props.classifications}
				taxOptions={this.props.taxOptions}
				customTaxRates={this.props.customTaxRates}
				clientId={this.props.clientId}
				hasTax={this.props.hasTax}
				onClassificationChange={this.onReclassifyTransaction}
				onTaxChange={this.props.onTaxChange}
				onCustomTaxRatesChange={this.props.onCustomTaxRatesChange}
				onMarkAsTransfer={this.onMarkReclassifiedAsTransfer}
				onClose={this.onCancelReclassifyTransaction}
			/>
		);
	};

	renderDecisionNoteSummary = decision => {
		const lastComment = decision.noteThread[decision.noteThread.length - 1];
		const earlierCount = decision.noteThread.length - 1;
		const earlierText = earlierCount > 0 ? ` (+${earlierCount} earlier)` : "";

		return (
			<div className="bk-review__note" onClick={event => event.stopPropagation()}>
				<Icon.MessageSquare size={13} />
				<span className="bk-review__note-text">
					<strong>{lastComment.author}:</strong> {lastComment.text}{earlierText}
				</span>
			</div>
		);
	};

	renderCommentThread = comments => {
		return (
			<div className="bk-review__note-thread">
				{comments.map((comment, index) => {
					const commentDate = UtilityService.formatDate({ value: comment.at, format: "YYYY-MM-DD", fallback: "" });
					return (
						<div key={index} className="bk-review__note-comment">
							<div className="bk-review__note-comment-header">
								<strong>{comment.author}</strong>
								<span className="bk-review__note-comment-date">{commentDate}</span>
							</div>
							<div className="bk-review__note-comment-text">{comment.text}</div>
						</div>
					);
				})}
			</div>
		);
	};

	renderCommentEditor = (target, hasExistingComments) => {
		const { reviewNoteDraft } = this.state;

		return (
			<div className="bk-review__note-editor" onClick={event => event.stopPropagation()}>
				<TextAreaField
					title={hasExistingComments ? "Reply" : "Describe the issue"}
					name="reviewNoteDraft"
					value={reviewNoteDraft}
					onChange={event => this.update({ reviewNoteDraft: event.target.value })}
					placeholder="Question or answer for the other side — e.g. attach the invoice for this."
				/>
				<div className="bk-review__note-editor-actions">
					<button type="button" className="tk-button tk-button--secondary tk-button--compact" onClick={() => this.onAddDecisionComment(target)} disabled={!reviewNoteDraft.trim()}>
						<Icon.Check size={14} />
						Add comment
					</button>
					<button type="button" className="tk-button tk-button--secondary tk-button--compact" onClick={this.onCancelDecisionNote}>Cancel</button>
				</div>
			</div>
		);
	};

	renderBulkIssueModal = () => {
		const { transactionsBeingFlagged, reviewNoteDraft } = this.state;
		if (!transactionsBeingFlagged) return null;

		const rowCount = transactionsBeingFlagged.length;
		const accountsCovered = [...new Set(transactionsBeingFlagged.map(transaction => transaction.classification || "Unclassified"))];

		const footer = (
			<>
				<span />
				<span className="tk-modal-actions">
					<button type="button" className="tk-button tk-button--secondary" onClick={this.onBulkIssueClose}>Cancel</button>
					<button type="button" className="tk-button tk-button--primary" onClick={this.onBulkIssueSubmit} disabled={!reviewNoteDraft.trim()}>
						Raise on {rowCount} transaction{rowCount === 1 ? "" : "s"}
					</button>
				</span>
			</>
		);

		return (
			<ToolsModal open={true} onClose={this.onBulkIssueClose} title={`Raise an issue on ${rowCount} transaction${rowCount === 1 ? "" : "s"}`} width={520} footer={footer}>
				<p className="bk-review__bulk-issue-note">
					The same comment goes on every one of them, across {accountsCovered.length} account{accountsCovered.length === 1 ? "" : "s"}.
					Each row keeps its own thread, so a reply on one does not answer the others.
				</p>
				<TextAreaField
					title="Describe the issue"
					name="reviewNoteDraft"
					value={reviewNoteDraft}
					onChange={event => this.update({ reviewNoteDraft: event.target.value })}
					placeholder="Question or answer for the other side — e.g. what do these payments cover?"
				/>
			</ToolsModal>
		);
	};

	renderIssueStatusBadge = status => {
		if (!status) return null;

		const statusOption = ISSUE_STATUSES.find(option => option.value === status);
		const statusLabel = statusOption ? statusOption.label : status;
		const badgeClass = clsx("bk-review__issue-badge", `bk-review__issue-badge--${status}`);
		return <span className={badgeClass}>{statusLabel}</span>;
	};

	renderDecisionRow = (decision, priorByAccount, hasGeneralLedger) => {
		const { expandedDecisionKeys, notingDecisionKey } = this.state;

		const isExpanded = !!expandedDecisionKeys[decision.decisionKey];
		const isNoting = notingDecisionKey === decision.decisionKey;
		const firstReviewedTransaction = decision.transactions.find(transaction => transaction.reviewed_by);
		const reviewerName = firstReviewedTransaction ? firstReviewedTransaction.reviewed_by : "";
		const totalAmountText = UtilityService.formatCurrency(decision.totalAmount);
		const taxText = decision.tax || "No tax";
		const transactionCountText = `${decision.transactions.length} transaction${decision.transactions.length === 1 ? "" : "s"}`;
		const decisionClass = clsx("bk-review__decision", { "bk-review__decision--exception": decision.isException });

		const isSelectable = decision.isUnclassifiedDecision || !decision.isFullyReviewed;
		const selectionState = this.getDecisionSelectionState(decision);

		const priorJoinKey = decision.canonicalCode || decision.classification;
		const priorTotal = priorByAccount ? priorByAccount[priorJoinKey] : null;
		const showPriorComparison = hasGeneralLedger && priorTotal != null;

		return (
			<div key={decision.decisionKey} className={decisionClass}>
				<div className="bk-review__decision-main" onClick={() => this.onToggleDecisionDetails(decision.decisionKey)}>
					{isSelectable && (
						<span className={clsx("bk-review__decision-checkbox", selectionState === "some" && "bk-review__decision-checkbox--partial")} onClick={event => event.stopPropagation()}>
							<Checkbox checked={selectionState !== "none"} onChange={() => this.onToggleDecisionSelected(decision)} />
						</span>
					)}
					<div className="bk-review__decision-info">
						<span className="bk-review__decision-name">{decision.classification}</span>
						<span className="bk-review__decision-amount-row">
							<span className="bk-review__decision-amount">${totalAmountText}</span>
							{showPriorComparison && this.renderPercentChange(decision.totalAmount, priorTotal, this.getDecisionAccountKind(decision))}
						</span>
						<span className="bk-review__decision-meta">{transactionCountText} · {taxText}</span>
					</div>
					{this.renderIssueStatusBadge(decision.issueStatus)}
					<div className="bk-review__decision-actions" onClick={event => event.stopPropagation()}>
						<button type="button" className="tk-button tk-button--secondary tk-button--compact tk-button--icon" onClick={() => this.onStartDecisionNote(decision)} title={decision.noteThread.length > 0 ? "View / reply to issue" : "Raise an issue"}>
							<Icon.MessageSquare size={14} />
						</button>
						{decision.isFullyReviewed ? (
							<>
								<span className="bk-review__reviewed-by">
									<Icon.CheckCircle size={14} /> {reviewerName}
								</span>
								<button type="button" className="tk-button tk-button--secondary tk-button--compact" onClick={() => this.onUndoDecisionReview(decision)}>Undo</button>
							</>
						) : (
							!decision.isUnclassifiedDecision && (
								<button type="button" className="tk-button tk-button--secondary tk-button--compact" onClick={() => this.onApproveDecision(decision)}>
									<Icon.Check size={14} />
									Approve
								</button>
							)
						)}
					</div>
				</div>
				{decision.noteThread.length > 0 && !isNoting && this.renderDecisionNoteSummary(decision)}
				{isNoting && (
					<div className="bk-review__note-block">
						{decision.noteThread.length > 0 && this.renderCommentThread(decision.noteThread)}
						{this.renderCommentEditor(decision, decision.noteThread.length > 0)}
					</div>
				)}
				{isExpanded && this.renderDecisionTransactions(decision)}
			</div>
		);
	};

	renderIssueCard = issue => {
		const isNoting = this.state.notingIssueKey === issue.issueKey;
		const statusSelectClass = clsx("bk-review__issue-status", `bk-review__issue-status--${issue.status}`);
		const commentCountText = `${issue.commentCount} comment${issue.commentCount === 1 ? "" : "s"}`;

		return (
			<div key={issue.issueKey} className="bk-review__issue-card">
				<div className="bk-review__issue-head">
					<div className="bk-review__issue-account-block">
						<span className="bk-review__issue-account">{issue.account}</span>
						{issue.rowLabel && <span className="bk-review__issue-row">{issue.rowLabel}</span>}
						<span className="bk-review__issue-count">{commentCountText}</span>
					</div>
					<select className={statusSelectClass} value={issue.status} onChange={event => this.onSetIssueStatus(issue, event.target.value)}>
						{ISSUE_STATUSES.map(option => (
							<option key={option.value} value={option.value}>{option.label}</option>
						))}
					</select>
				</div>
				{this.renderCommentThread(issue.comments)}
				{isNoting ? this.renderCommentEditor(issue, true) : (
					<button type="button" className="tk-button tk-button--secondary tk-button--compact" onClick={() => this.onStartIssueReply(issue)}>
						<Icon.MessageSquare size={14} />
						Reply
					</button>
				)}
			</div>
		);
	};

	renderRaiseIssueForm = decisions => {
		const { reviewNoteDraft, raiseIssueDecisionKey } = this.state;

		const accountOptions = decisions.map((decision, index) => ({ id: index, name: decision.classification, decisionKey: decision.decisionKey }));
		const selectedDecision = decisions.find(decision => decision.decisionKey === raiseIssueDecisionKey);
		const selectedName = selectedDecision ? selectedDecision.classification : "";
		const canSubmit = !!raiseIssueDecisionKey && reviewNoteDraft.trim() !== "";

		return (
			<div className="bk-review__raise-issue">
				<SearchableDropdown
					title="Account"
					options={accountOptions}
					selectedName={selectedName}
					onSelect={this.onSelectRaiseIssueAccount}
					showAll
					firstFocus
				/>
				<TextAreaField
					title="Describe the issue"
					name="reviewNoteDraft"
					value={reviewNoteDraft}
					onChange={event => this.update({ reviewNoteDraft: event.target.value })}
					placeholder="What needs attention on this account?"
				/>
				<div className="bk-review__note-editor-actions">
					<button type="button" className="tk-button tk-button--secondary tk-button--compact" onClick={() => this.onRaiseIssueSubmit(decisions)} disabled={!canSubmit}>
						<Icon.Check size={14} />
						Raise issue
					</button>
					<button type="button" className="tk-button tk-button--secondary tk-button--compact" onClick={this.onCancelRaiseIssue}>Cancel</button>
				</div>
			</div>
		);
	};

	renderIssuesView = (issues, decisions) => {
		const { raisingIssueOpen } = this.state;

		return (
			<div className="bk-review__issues">
				<div className="bk-review__issues-header">
					<button type="button" className="tk-button tk-button--secondary tk-button--compact" onClick={this.closeIssuesView}>
						<Icon.ArrowLeft size={16} />
						Back to review
					</button>
					<span className="bk-review__issues-title">Issues ({issues.length})</span>
					<span className="bk-review__bulk-spacer" />
					{!raisingIssueOpen && (
						<button type="button" className="tk-button tk-button--secondary tk-button--compact" onClick={this.onStartRaiseIssue}>
							<Icon.Plus size={14} />
							Raise issue
						</button>
					)}
				</div>
				{raisingIssueOpen && this.renderRaiseIssueForm(decisions)}
				<div className="bk-review__list">
					{issues.length === 0 && !raisingIssueOpen && <p className="bk-review__empty">No issues raised yet. Use "Raise issue" above, or the message icon on an account.</p>}
					{issues.map(this.renderIssueCard)}
				</div>
			</div>
		);
	};

	render() {
		const { transactions, generalLedgerEntries, onClose } = this.props;
		const { reviewSearchText, reviewStatusFilter, selectedTrialBalanceCode, issuesViewOpen, reviewSectionOpen } = this.state;

		const hasGeneralLedger = (generalLedgerEntries || []).length > 0;
		const modalWidth = hasGeneralLedger ? 1320 : 900;

		const currentPeriod = BookkeepingService.describeDatedPeriod({ rows: transactions, fiscalYearEnd: this.props.fiscalYearEnd });
		const priorPeriod = BookkeepingService.describeDatedPeriod({ rows: generalLedgerEntries });

		const decisions = BookkeepingService.buildReviewDecisions({ transactions });
		const reviewedDecisions = decisions.filter(decision => decision.isFullyReviewed);
		const progressText = `${reviewedDecisions.length} of ${decisions.length} decisions reviewed`;
		const currentByAccount = this.getCurrentTotalsByAccount(decisions);
		const issues = BookkeepingService.getReviewIssues({ decisions });

		const isAccountSelected = selectedTrialBalanceCode !== "";
		const decisionsForSelectedAccount = isAccountSelected ? BookkeepingService.getDecisionsForCanonicalCode({ decisions, canonicalCode: selectedTrialBalanceCode }) : decisions;
		const filteredDecisions = BookkeepingService.filterReviewDecisions({
			decisions: decisionsForSelectedAccount,
			searchText: isAccountSelected ? "" : reviewSearchText,
			reviewStatus: reviewStatusFilter,
		});
		const isSearching = reviewSearchText.trim() !== "" || isAccountSelected;
		const filteredGlEntries = isAccountSelected
			? BookkeepingService.filterGeneralLedgerEntriesByCanonicalCode({ entries: generalLedgerEntries, canonicalCode: selectedTrialBalanceCode })
			: BookkeepingService.filterGeneralLedgerEntries({ entries: generalLedgerEntries, searchText: reviewSearchText });
		const priorGroups = BookkeepingService.groupGeneralLedgerEntriesByAccount({ entries: filteredGlEntries });
		const priorByAccount = {};
		priorGroups.forEach(group => { priorByAccount[group.canonicalCode || group.account] = group.totalAmount; });
		const selectableDecisions = this.getSelectableDecisions(filteredDecisions);
		const selectableTransactions = selectableDecisions.flatMap(decision => decision.transactions);
		const selectedTransactionCount = this.getSelectedTransactions(selectableDecisions).length;
		const selectedApprovableCount = this.getSelectedTransactions(this.getApprovableDecisions(filteredDecisions)).length;
		const everySelectableIsSelected = selectableTransactions.length > 0 && selectedTransactionCount === selectableTransactions.length;
		const everythingExpanded = this.isEverythingExpanded(filteredDecisions, priorGroups);

		const footer = (
			<>
				<span />
				<span className="tk-modal-actions">
					<button type="button" className="tk-button tk-button--secondary" onClick={onClose}>Done</button>
				</span>
			</>
		);

		return (
			<>
			<ToolsModal open={true} onClose={onClose} title="Review allocations" width={modalWidth} footer={footer}>
				{issuesViewOpen ? this.renderIssuesView(issues, decisions) : (
				<>
				<div className="bk-review__filters">
					<div className="tk-search bk-review__search">
						<Icon.Search size={15} className="tk-search__icon" />
						<input
							className="tk-search__input"
							placeholder={hasGeneralLedger ? "Search both years…" : "Search accounts…"}
							value={reviewSearchText}
							onChange={e => this.update({ reviewSearchText: e.target.value, selectedTrialBalanceCode: "" })}
						/>
					</div>
					<span className="bk-review__filter-select">
						<ToolsSelect
							value={reviewStatusFilter}
							options={REVIEW_STATUS_FILTER_OPTIONS}
							onChange={status => this.update({ reviewStatusFilter: status })}
						/>
					</span>
					<button type="button" className="tk-button tk-button--secondary tk-button--compact" onClick={this.openIssuesView}>
						<Icon.AlertCircle size={16} />
						Issues ({issues.length})
					</button>
				</div>
				{this.renderTrialBalancePanel()}
				<div className="bk-review__panel">
					<button type="button" className="bk-review__panel-toggle" onClick={this.onToggleReviewSection}>
						{reviewSectionOpen ? <Icon.ChevronDown size={16} /> : <Icon.ChevronRight size={16} />}
						<span className="bk-review__panel-title">Allocations</span>
						<span className="bk-review__panel-status">{progressText}</span>
					</button>
					{reviewSectionOpen && (
					<div className="bk-review__panel-body">
					<div className={clsx("bk-review__layout", hasGeneralLedger && "bk-review__layout--split")}>
						<div className="bk-review__col">
						{hasGeneralLedger && (
							<div className="bk-review__col-header">
								<span className="bk-review__col-title">Current year{currentPeriod.yearName ? ` (${currentPeriod.yearName})` : ""}</span>
								{currentPeriod.span && <span className="bk-review__col-subtitle">{currentPeriod.span}</span>}
							</div>
						)}
						<div className="bk-review__bulk-bar">
						<Checkbox
							title={`Select all (${selectableTransactions.length})`}
							checked={everySelectableIsSelected}
							onChange={() => this.onToggleSelectAllDecisions(filteredDecisions)}
						/>
						{selectedTransactionCount > 0 && (
							<button type="button" className="tk-button tk-button--secondary tk-button--compact" onClick={() => this.onClassifySelectedClicked(filteredDecisions)}>
								<Icon.Tag size={14} />
								Classify ({selectedTransactionCount})
							</button>
						)}
						{selectedTransactionCount > 0 && (
							<button type="button" className="tk-button tk-button--secondary tk-button--compact" onClick={() => this.onRaiseIssueOnSelectedClicked(filteredDecisions)}>
								<Icon.MessageSquare size={14} />
								Raise issue ({selectedTransactionCount})
							</button>
						)}
						{selectedApprovableCount > 0 && (
							<button type="button" className="tk-button tk-button--secondary tk-button--compact" onClick={() => this.onApproveSelectedDecisions(filteredDecisions)}>
								<Icon.CheckCircle size={14} />
								Approve ({selectedApprovableCount})
							</button>
						)}
						<span className="bk-review__bulk-spacer" />
						<button type="button" className="tk-button tk-button--secondary tk-button--compact" onClick={() => this.onToggleExpandAll(filteredDecisions, priorGroups)}>
							{everythingExpanded ? <Icon.ChevronsUp size={14} /> : <Icon.ChevronsDown size={14} />}
							{everythingExpanded ? "Collapse all" : "Expand all"}
						</button>
					</div>
					<div className="bk-review__list">
						{filteredDecisions.length === 0 && <p className="bk-review__empty">No decisions match the filters.</p>}
						{filteredDecisions.map(decision => this.renderDecisionRow(decision, priorByAccount, hasGeneralLedger))}
					</div>
					</div>
					{hasGeneralLedger && this.renderGeneralLedgerPanel(priorGroups, currentByAccount, priorPeriod, isSearching)}
				</div>
				</div>
				)}
			</div>
				</>
				)}
			</ToolsModal>
			{this.renderReclassifyModal()}
			{this.renderBulkIssueModal()}
			{this.state.transactionsBeingClassified && (
				<BulkClassifyModal
					transactions={this.state.transactionsBeingClassified}
					classifications={this.props.classifications}
					taxOptions={this.props.taxOptions}
					hasTax={this.props.hasTax}
					onApply={this.onBulkClassifyApply}
					onClose={this.onBulkClassifyClose}
				/>
			)}
			</>
		);
	}
}

export default AllocationReviewModal;
